import {
  Box,
  Flex,
  Text,
  Checkbox,
  Progress,
  Radio,
  RadioGroup,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import Field from "components/Input/Field";
import ButtonPrimary from "components/Button/PrimaryButton";
import OutlineButton from "components/Button/OutlineButton";
import { ParametroCobrancaForm } from "features/condominio/typings";
import useParametrosCobranca from "features/condominio/hooks/useParametrosCobranca";
import { useEffect } from "react";
import SelectContaCategoria from "components/Input/SelectContaCategoria";
import SelectTiposIndiceMonetario from "components/Input/SelectTiposIndiceMonetario";
import SelectBaseAntd from "components/Input/SelectBaseAntd";
import { getOptionsCompetencia, getOptionsTipoVencimento } from "./util/helper";
import { Col, Row } from "antd";
import InputMoney from "components/Input/InputMoney";
import AddRemoveLineButton from "components/Button/AddRemoveLineButton";
import useSaveParametroCobranca from "features/condominio/hooks/useSaveParametroCobranca";
import CardDefault from "components/Cards/CardDefault";
import { useLoading } from "components/GlobalLoading";
import { showSuccess } from "infra/helpers/alerta";
import { ParametroCobrancaRequest } from "core/features/condominio/typings/index";
import { OperacaoEnum } from "resources/enums/conta";
import { tagSetorJuridicoString } from "features/conta/typings";
import SelectFormaPagamento from "components/Input/SelectFormaPagamento";
import useGetTenantIntegracoes from "core/features/tenants/hooks/useGetTenantIntegracoes";
import colors from "core/resources/theme/colors";
import ButtonAnexarArquivosTemp from "components/Button/ButtonAnexarArquivosTemp";

const defaultValues: Partial<ParametroCobrancaForm> = {
  proRataDia: false,
  enviarArrecadacaoEmail: "s",
  enviarArrecadacaoPush: "n",
  jurosHonorario: [
    {
      id: new Date().getTime().toString(),
      diasInadimplencia: 0,
      honorario: true,
      valor: 0,
      juridico: false,
    },
  ],
  jurosMensal: [
    {
      id: new Date().getTime().toString(),
      diasInadimplencia: 1,
      honorario: false,
      valor: 0,
    },
  ],
};

interface ParametrosCobrancaFormProps {
  tenant?: string;
  onClose?: () => any;
  textSalvar?: string;
}

export default function ParametrosCobrancaForm({
  tenant,
  onClose,
  textSalvar = "Salvar alterações",
}: ParametrosCobrancaFormProps) {
  const { control, handleSubmit, watch, reset, setValue, getValues } =
    useForm<ParametroCobrancaForm>({
      mode: "all",
      reValidateMode: "onChange",

      defaultValues,
    });

  const {
    data: parametrosCobranca,
    isLoading,
    isFetching,
  } = useParametrosCobranca(tenant);

  const { data: integracoes, isLoading: isLoadingIntegracoes } =
    useGetTenantIntegracoes();

  const { mutate: salvarParametros, isLoading: isLoadingSave } =
    useSaveParametroCobranca(tenant);

  useEffect(() => {
    if (parametrosCobranca) {
      const jurosHonorario = parametrosCobranca.juros
        .filter((juros) => juros.honorario)
        .concat(defaultValues.jurosHonorario!)
        .slice(0, -1);

      const jurosMensal = parametrosCobranca.juros
        .filter((juros) => !juros.honorario)
        .concat(defaultValues.jurosMensal!)
        .slice(0, -1);

      reset({
        ...parametrosCobranca,
        jurosHonorario,
        jurosMensal,
        enviarArrecadacaoEmail:
          parametrosCobranca.enviarArrecadacaoEmail ||
          parametrosCobranca.enviarArrecadacaoEmail === undefined
            ? "s"
            : "n",
        enviarArrecadacaoPush: parametrosCobranca.enviarArrecadacaoPush
          ? "s"
          : "n",
        protestoAutomaticoBoleto:
          !!parametrosCobranca.diasParaProtestar &&
          parametrosCobranca.diasParaProtestar > 0,
        certificadoDigitalArquivoId: parametrosCobranca.certificadoDigital?.id,
        certificadoDigitalExisteSenha:
          parametrosCobranca.certificadoDigital?.existeSenha,
        certificadoDigitalNome: parametrosCobranca.certificadoDigital?.nome,
      });
    }
  }, [parametrosCobranca, reset]);

  const _isLoading =
    isLoading || isFetching || isLoadingSave || isLoadingIntegracoes;

  const form = watch();

  const onAddJuros = (juros: "jurosHonorario") => {
    const _form = getValues();

    setValue(
      juros,
      _form[juros].concat([
        {
          id: new Date().getTime().toString(),
          honorario: juros === "jurosHonorario",
          valor: 0,
          diasInadimplencia: 0,
          juridico: undefined,
        },
      ]),
    );
  };

  const onRemoveJuros = (juros: "jurosHonorario") => {
    const _form = getValues();
    _form[juros].pop();
    setValue(juros, _form[juros]);
  };

  const onSubmit = (data: ParametroCobrancaForm) => {
    const jurosMensal = data.jurosMensal.map((juros) => {
      if (Number.isInteger(Number(juros.id))) {
        delete juros.id;
      }
      return { ...juros, diasInadimplencia: 1 };
    });
    const jurosHonorario = data.jurosHonorario.map((juros) => {
      if (Number.isInteger(Number(juros.id))) {
        delete juros.id;
      }
      return juros;
    });

    data.juros = jurosMensal.concat(jurosHonorario);

    const _data: ParametroCobrancaRequest = {
      ...data,
      enviarArrecadacaoEmail:
        data.enviarArrecadacaoEmail === "s" ? true : false,
      enviarArrecadacaoPush: data.enviarArrecadacaoPush === "s" ? true : false,
    };

    salvarParametros(_data, {
      onSuccess: async () => {
        await showSuccess();
        onClose?.();
      },
    });
  };

  useLoading(onClose ? _isLoading : false);

  return (
    <CardDefault
      mb={8}
      bg="transparent"
      shadow={"none"}
      p="0px"
      borderRadius="md"
    >
      <form onSubmit={(ev) => handleSubmit(onSubmit)(ev)}>
        {_isLoading && !onClose ? (
          <CardDefault mb={4}>
            <Progress size="sm" isIndeterminate w="100%" colorScheme="cyan" />
          </CardDefault>
        ) : null}

        <CardDefault mb={6}>
          <Title text="Vencimentos e Boletos" />

          <Box as={Row} gutter={16} mb={6}>
            <Box as={Col} xs={18}>
              <Field
                control={control}
                label="Forçar vencimento dia útil"
                name="tipoVencimentoCobrancaId"
                render={({ field, isInvalid }) => (
                  <SelectBaseAntd
                    placeholder="Selecione"
                    size="large"
                    options={getOptionsTipoVencimento()}
                    isInvalid={isInvalid}
                    {...field}
                  />
                )}
              />
            </Box>

            <Box as={Col} xs={6}>
              <Field
                control={control}
                label="Dia de vencimento"
                name="diaVencimento"
                render={({ field, isInvalid }) => (
                  <InputMoney
                    allowDecimals={false}
                    placeholder="--"
                    style={{ textAlign: "left" }}
                    decimalScale={0}
                    minValue={1}
                    maxValue={31}
                    isInvalid={isInvalid}
                    {...field}
                  />
                )}
              />
            </Box>
          </Box>

          <Box as={Row} gutter={16}>
            <Box as={Col} xs={24}>
              <Field
                control={control}
                name="gerarBoletoUnicoNormaisExtra"
                render={({ field, isInvalid }) => (
                  <Checkbox
                    size="lg"
                    isChecked={field.value}
                    isInvalid={isInvalid}
                    onChange={() => field.onChange(!field.value)}
                  >
                    Gerar um boleto único para arrecadações normais e extras
                  </Checkbox>
                )}
              />
            </Box>
          </Box>
        </CardDefault>

        <CardDefault mb={6}>
          <Title text="Envio de cobrança automática no e-mail?" />
          <Box>
            <Field
              control={control}
              name="enviarArrecadacaoEmail"
              rules={{
                required: true,
              }}
              render={({ field, isInvalid }) => (
                <RadioGroup value={field.value} onChange={field.onChange}>
                  <Radio isInvalid={isInvalid} value={"s"} mr={8}>
                    Sim
                  </Radio>
                  <Radio isInvalid={isInvalid} value={"n"}>
                    Não
                  </Radio>
                </RadioGroup>
              )}
            />
          </Box>
        </CardDefault>

        <CardDefault mb={6}>
          <Title text="Notificar a cobrança automaticamente no aplicativo?" />
          <Box>
            <Field
              control={control}
              name="enviarArrecadacaoPush"
              rules={{
                required: true,
              }}
              render={({ field, isInvalid }) => (
                <RadioGroup value={field.value} onChange={field.onChange}>
                  <Radio isInvalid={isInvalid} value={"s"} mr={8}>
                    Sim
                  </Radio>
                  <Radio isInvalid={isInvalid} value={"n"}>
                    Não
                  </Radio>
                </RadioGroup>
              )}
            />
          </Box>
        </CardDefault>

        <CardDefault mb={6}>
          <Title text="Cobrança automática no cartão" />

          <Box as={Row} gutter={16}>
            <Box as={Col} xs={8}>
              <Field
                control={control}
                name="cobrancaAutomaticaCartao"
                render={({ field, isInvalid }) => (
                  <Checkbox
                    size="lg"
                    mt={6}
                    isChecked={field.value}
                    isInvalid={isInvalid}
                    onChange={() => field.onChange(!field.value)}
                  >
                    Ativar cobrança automática no cartão?
                  </Checkbox>
                )}
              />
            </Box>

            {form.cobrancaAutomaticaCartao ? (
              <Box as={Col} xs={16}>
                <Field
                  control={control}
                  label="Quantos dias antes do vencimento?"
                  name="cobrancaAutomaticaCartaoDiasAntesVencimento"
                  render={({ field, isInvalid }) => (
                    <InputMoney
                      allowDecimals={false}
                      placeholder="--"
                      style={{ textAlign: "left" }}
                      decimalScale={0}
                      allowNegativeValue={false}
                      isInvalid={isInvalid}
                      {...field}
                      allowUndefined
                    />
                  )}
                />
              </Box>
            ) : null}
          </Box>
        </CardDefault>

        <CardDefault mb={6}>
          <Title text="Métodos de cobrança padrão" />

          <Box as={Row} gutter={16}>
            <Box as={Col} xs={12}>
              <Field
                label="Arrecadação"
                control={control}
                name="formasRecebimentoPadraoArrecadacaoIds"
                rules={{
                  required: true,
                }}
                render={({ field, isInvalid }) => (
                  <SelectFormaPagamento
                    isInvalid={isInvalid}
                    placeholder="Selecione uma ou mais"
                    mode="multiple"
                    queryParams={{
                      aceitaRecebimentoOnline: true,
                      ativo: true,
                    }}
                    {...field}
                  />
                )}
              />
            </Box>

            <Box as={Col} xs={12}>
              <Field
                label="Acordo"
                control={control}
                name="formasRecebimentoPadraoAcordoIds"
                rules={{
                  required: true,
                }}
                render={({ field, isInvalid }) => (
                  <SelectFormaPagamento
                    isInvalid={isInvalid}
                    placeholder="Selecione uma ou mais"
                    mode="multiple"
                    queryParams={{
                      aceitaRecebimentoOnline: true,
                      ativo: true,
                    }}
                    {...field}
                  />
                )}
              />
            </Box>
          </Box>
        </CardDefault>

        <CardDefault mb={6}>
          <Title text="Após quantos dias sem pagamento depois da primeira parcela o acordo será invalidado?" />

          <Box as={Row}>
            <Box as={Col} xs={6}>
              <Field
                control={control}
                label="Quantidade de dias"
                name="diasCancelamentoAcordo"
                render={({ field, isInvalid }) =>
                  isLoading || isFetching ? (
                    <InputMoney
                      allowDecimals={false}
                      placeholder="--"
                      style={{ textAlign: "left" }}
                      decimalScale={0}
                      allowNegativeValue={false}
                      isInvalid={isInvalid}
                      value={undefined}
                      disabled
                      onChange={() => {}}
                      allowUndefined
                    />
                  ) : (
                    <InputMoney
                      allowDecimals={false}
                      placeholder="--"
                      style={{ textAlign: "left" }}
                      decimalScale={0}
                      allowNegativeValue={false}
                      isInvalid={isInvalid}
                      {...field}
                      allowUndefined
                    />
                  )
                }
              />
            </Box>
          </Box>
        </CardDefault>

        {integracoes?.protestoBoleto ? (
          <CardDefault mb={6}>
            <Title text="Protesto automático de boletos" />

            <Box as={Row} gutter={16} mt={-4}>
              <Box as={Col} xs={8}>
                <Field
                  control={control}
                  name="protestoAutomaticoBoleto"
                  render={({ field, isInvalid }) => (
                    <Checkbox
                      size="lg"
                      mt={6}
                      isChecked={field.value}
                      isInvalid={isInvalid}
                      onChange={() => field.onChange(!field.value)}
                    >
                      Ativar protesto automático de boletos?
                    </Checkbox>
                  )}
                />
              </Box>

              {form.protestoAutomaticoBoleto ? (
                <Box as={Col} xs={16}>
                  <Field
                    control={control}
                    label="Quantos dias após o vencimento?"
                    name="diasParaProtestar"
                    render={({ field, isInvalid }) => (
                      <InputMoney
                        allowDecimals={false}
                        placeholder="--"
                        style={{ textAlign: "left" }}
                        decimalScale={0}
                        allowNegativeValue={false}
                        isInvalid={isInvalid}
                        {...field}
                        allowUndefined
                      />
                    )}
                  />
                </Box>
              ) : null}
            </Box>

            {form.protestoAutomaticoBoleto ? (
              <Box as={Row} gutter={16} mt={2}>
                <Box as={Col} xs={8}>
                  <Field
                    control={control}
                    label="Certificado digital"
                    name="certificadoDigitalId"
                    shouldUnregister
                    render={() => {
                      return (
                        <Flex flexDir="column">
                          <ButtonAnexarArquivosTemp
                            multiple={false}
                            accept=".pem, .crt, .cer, .pfx, .p12, .p7b, .p7c"
                            maxFiles={1}
                            maxFileSizeMb={5}
                            variant="outline"
                            buttonText="Anexar"
                            currentFilesLength={0}
                            fileName={form.certificadoDigitalNome}
                            showLoadingModal={false}
                            onFinishUploading={(arquivos) => {
                              const arquivo = arquivos[0];
                              if (arquivo) {
                                setValue(
                                  "certificadoDigitalArquivoId",
                                  arquivo.id,
                                );
                                setValue(
                                  "certificadoDigitalNome",
                                  arquivo.nome,
                                );
                              }
                            }}
                          />
                        </Flex>
                      );
                    }}
                  />
                </Box>

                <Box as={Col} xs={16}>
                  <Field
                    control={control}
                    label="Senha do certificado"
                    name="certificadoDigitalSenha"
                    shouldUnregister
                    inputProps={{ type: "password" }}
                  />
                  {form?.certificadoDigitalExisteSenha ? (
                    <Text color={colors.accentPositive} fontSize={10} mt={2}>
                      Já existe uma senha configurada.
                    </Text>
                  ) : null}
                </Box>
              </Box>
            ) : null}
          </CardDefault>
        ) : null}

        <CardDefault mb={6}>
          <Title text="Categorizações padrões" />

          <Box as={Row} gutter={16} mb={6}>
            <Box as={Col} xs={12}>
              <Field
                control={control}
                label="Categoria do Acordo"
                name="categoriaAcordoId"
                rules={{ required: true }}
                render={({ field, isInvalid }) => (
                  <SelectContaCategoria
                    disableNotClassifiableNodes
                    placeholder="Selecione"
                    size="large"
                    isInvalid={isInvalid}
                    {...field}
                  />
                )}
              />
            </Box>
          </Box>

          <Box as={Row} gutter={16} mb={6}>
            <Box as={Col} xs={12}>
              <Field
                control={control}
                label="Juros - Receitas"
                name="categoriaJurosId"
                rules={{ required: true }}
                render={({ field, isInvalid }) => (
                  <SelectContaCategoria
                    disableNotClassifiableNodes
                    placeholder="Selecione"
                    size="large"
                    isInvalid={isInvalid}
                    {...field}
                  />
                )}
              />
            </Box>

            <Box as={Col} xs={12}>
              <Field
                control={control}
                label="Multa - Receitas"
                name="categoriaMultaId"
                rules={{ required: true }}
                render={({ field, isInvalid }) => (
                  <SelectContaCategoria
                    disableNotClassifiableNodes
                    placeholder="Selecione"
                    size="large"
                    isInvalid={isInvalid}
                    {...field}
                  />
                )}
              />
            </Box>
          </Box>

          <Box as={Row} gutter={16} mb={6}>
            <Box as={Col} xs={12}>
              <Field
                control={control}
                label="Juros - Despesas"
                name="categoriaJurosDespesaId"
                render={({ field, isInvalid }) => (
                  <SelectContaCategoria
                    disableNotClassifiableNodes
                    placeholder="Selecione"
                    size="large"
                    isInvalid={isInvalid}
                    {...field}
                  />
                )}
              />
            </Box>

            <Box as={Col} xs={12}>
              <Field
                control={control}
                label="Multa - Despesas"
                name="categoriaMultaDespesaId"
                render={({ field, isInvalid }) => (
                  <SelectContaCategoria
                    disableNotClassifiableNodes
                    placeholder="Selecione"
                    size="large"
                    isInvalid={isInvalid}
                    {...field}
                  />
                )}
              />
            </Box>
          </Box>

          <Box as={Row} gutter={16} mb={6}>
            <Box as={Col} xs={12}>
              <Field
                control={control}
                label="Categoria de envio padrão p/ Transferência"
                name="categoriaTransferenciaEnvioId"
                render={({ field, isInvalid }) => (
                  <SelectContaCategoria
                    disableNotClassifiableNodes
                    placeholder="Selecione"
                    size="large"
                    operacao={OperacaoEnum.Despesa}
                    isInvalid={isInvalid}
                    {...field}
                  />
                )}
              />
            </Box>

            <Box as={Col} xs={12}>
              <Field
                control={control}
                label="Categoria de recebimento padrão p/ Transferência"
                name="categoriaTransferenciaRecebimentoId"
                render={({ field, isInvalid }) => (
                  <SelectContaCategoria
                    disableNotClassifiableNodes
                    placeholder="Selecione"
                    size="large"
                    isInvalid={isInvalid}
                    operacao={OperacaoEnum.Receita}
                    {...field}
                  />
                )}
              />
            </Box>
          </Box>

          <Box as={Row} gutter={16}>
            <Box as={Col} xs={12}>
              <Field
                control={control}
                label="Taxa de registro para cobrança online"
                name="categoriaRegistroBoletoId"
                rules={{
                  required: true,
                }}
                render={({ field, isInvalid }) => (
                  <SelectContaCategoria
                    disableNotClassifiableNodes
                    placeholder="Selecione"
                    operacao={OperacaoEnum.Despesa}
                    size="large"
                    isInvalid={isInvalid}
                    {...field}
                  />
                )}
              />
            </Box>

            <Box as={Col} xs={12}>
              <Field
                control={control}
                label="Categoria de Desconto"
                name="categoriaGrupoDescontoId"
                render={({ field, isInvalid }) => (
                  <SelectContaCategoria
                    disableNotClassifiableNodes
                    placeholder="Selecione"
                    operacao={OperacaoEnum.Despesa}
                    size="large"
                    isInvalid={isInvalid}
                    {...field}
                  />
                )}
              />
            </Box>
          </Box>
        </CardDefault>

        <CardDefault mb={6}>
          <Title text="Arrecadações e acréscimos moratórios" />

          <Box as={Row} gutter={16} mb={6}>
            <Box as={Col} xs={12}>
              <Field
                control={control}
                label="Competência das arrecadações"
                name="tipoCompetenciaCobrancaId"
                render={({ field, isInvalid }) => (
                  <SelectBaseAntd
                    placeholder="Selecione"
                    size="large"
                    options={getOptionsCompetencia()}
                    isInvalid={isInvalid}
                    {...field}
                  />
                )}
              />
            </Box>

            <Box as={Col} xs={12}>
              <Box as={Row} gutter={16}>
                <Box as={Col} xs={12}>
                  <Field
                    control={control}
                    label={"Taxa de juros mensal (%)"}
                    name={`jurosMensal.0.valor`}
                    render={({ field, isInvalid }) => (
                      <InputMoney
                        placeholder="--"
                        style={{ textAlign: "left" }}
                        isInvalid={isInvalid}
                        {...field}
                      />
                    )}
                  />
                </Box>

                <Box as={Col} xs={12}>
                  <Field
                    control={control}
                    label="Taxa de multa (%)"
                    name="taxaMulta"
                    render={({ field, isInvalid }) => (
                      <InputMoney
                        allowDecimals={false}
                        placeholder="--"
                        style={{ textAlign: "left" }}
                        decimalScale={0}
                        isInvalid={isInvalid}
                        {...field}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box as={Row} gutter={16} mt={-2}>
            <Box as={Col} xs={24}>
              <Field
                control={control}
                name="proRataDia"
                render={({ field, isInvalid }) => (
                  <Checkbox
                    size="lg"
                    isChecked={field.value}
                    isInvalid={isInvalid}
                    onChange={() => field.onChange(!field.value)}
                  >
                    Pro rata dia
                  </Checkbox>
                )}
              />
            </Box>
          </Box>
        </CardDefault>

        <CardDefault mb={6}>
          <Title text="Atualização monetária" />

          <Box as={Row} gutter={16}>
            <Box as={Col} xs={8}>
              <Field
                control={control}
                label="Atualização monetária após (dias)"
                name="diasProcessarAtualizacaoMoneraria"
                render={({ field, isInvalid }) => (
                  <InputMoney
                    allowDecimals={false}
                    placeholder="--"
                    style={{ textAlign: "left" }}
                    decimalScale={0}
                    isInvalid={isInvalid}
                    {...field}
                  />
                )}
              />
            </Box>

            <Box as={Col} xs={8}>
              <Field
                control={control}
                label="Índice"
                name="tipoIndiceMonetarioId"
                render={({ field, isInvalid }) => (
                  <SelectTiposIndiceMonetario
                    placeholder="Selecione"
                    size="large"
                    isInvalid={isInvalid}
                    {...field}
                  />
                )}
              />
            </Box>

            <Box as={Col} xs={8}>
              <Field
                control={control}
                label="Categoria de correção monetária"
                name="categoriaCorrecaoMonetariaId"
                render={({ field }) => (
                  <SelectContaCategoria
                    disableNotClassifiableNodes
                    placeholder="Selecione"
                    operacao={OperacaoEnum.Receita}
                    size="large"
                    allowClear
                    {...field}
                  />
                )}
              />
            </Box>
          </Box>
        </CardDefault>

        <CardDefault mb={6}>
          <Title text="Taxas de cobrança" />

          <Box
            mb={4}
            p={3}
            bg="blue.50"
            borderLeft="4px solid"
            borderColor="blue.400"
            borderRadius="md"
          >
            <Text fontSize="sm" color="blue.800">
              A cobrança será cancelada assim que iniciar a cobrança das taxas.
            </Text>
          </Box>

          {form.jurosHonorario.map((juros, i) => (
            <Box
              as={Row}
              gutter={16}
              mb={i === form.jurosHonorario?.length - 1 ? 0 : 4}
              key={juros.id}
            >
              <Box as={Col} xs={5}>
                <Field
                  control={control}
                  rules={{
                    required: true,
                  }}
                  label={i === 0 ? "Taxa de cobrança (%)" : undefined}
                  name={`jurosHonorario.${i}.valor`}
                  render={({ field, isInvalid }) => (
                    <InputMoney
                      placeholder="--"
                      style={{ textAlign: "left" }}
                      isInvalid={isInvalid}
                      {...field}
                    />
                  )}
                />
              </Box>

              <Box as={Col} xs={5}>
                <Field
                  control={control}
                  rules={{
                    required: true,
                  }}
                  label={i === 0 ? "Cobrar após quantos dias?" : undefined}
                  name={`jurosHonorario.${i}.diasInadimplencia`}
                  render={({ field, isInvalid }) => (
                    <InputMoney
                      allowDecimals={false}
                      placeholder="--"
                      style={{ textAlign: "left" }}
                      decimalScale={0}
                      isInvalid={isInvalid}
                      {...field}
                    />
                  )}
                />
              </Box>

              <Box as={Col} xs={8}>
                <Field
                  control={control}
                  rules={{
                    required: true,
                  }}
                  label={i === 0 ? "Categoria" : undefined}
                  name={`jurosHonorario.${i}.categoriaFinanceiraId`}
                  render={({ field, isInvalid }) => (
                    <SelectContaCategoria
                      disableNotClassifiableNodes
                      placeholder="Selecione"
                      size="large"
                      isInvalid={isInvalid}
                      {...field}
                    />
                  )}
                />
              </Box>

              <Box as={Col} xs={4}>
                <Field
                  control={control}
                  name={`jurosHonorario.${i}.juridico`}
                  render={({ field, isInvalid }) => (
                    <Checkbox
                      mt={i > 0 ? 2 : 7}
                      isChecked={field.value}
                      isInvalid={isInvalid}
                      onChange={() => {
                        field.onChange(!field.value);
                      }}
                    >
                      {tagSetorJuridicoString} ?
                    </Checkbox>
                  )}
                />
              </Box>

              <Box as={Col} xs={2}>
                <AddRemoveLineButton
                  size={7}
                  showRemove={i !== 0 && i === form.jurosHonorario.length - 1}
                  showAdd={i === form.jurosHonorario.length - 1}
                  onAdd={() => onAddJuros("jurosHonorario")}
                  onRemove={() => onRemoveJuros("jurosHonorario")}
                  containerProps={{ mt: i === 0 ? 2.5 : 0, pt: 0 }}
                />
              </Box>
            </Box>
          ))}
        </CardDefault>

        <Flex
          mt="32px"
          gridGap={4}
          justifyContent="flex-end"
          mb={onClose ? -6 : undefined}
        >
          {onClose ? (
            <OutlineButton onClick={onClose}>Cancelar</OutlineButton>
          ) : null}

          <ButtonPrimary type="submit" isLoading={_isLoading}>
            {textSalvar}
          </ButtonPrimary>
        </Flex>
      </form>
    </CardDefault>
  );
}

const Title = ({ text }: { text: string }) => (
  <Text mt={-2} mb={4} fontSize={18} fontWeight="bold">
    {text}
  </Text>
);
